import { useEffect, useState } from 'react';
import { ApiError, api } from '../api';
import { Banner, Pill, Spinner, useAsync } from '../components/Common';

interface LookupColumn {
  name: string;
  label?: string;
  type?: 'string' | 'number' | 'boolean';
  required?: boolean;
}

interface LookupTable {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  columns: LookupColumn[];
  rowCount: number;
}

/**
 * Managed lists.
 *
 * The fee-earner include-list is the one that matters: it decides who appears in
 * the Consultant Report and how each person maps to Bullhorn and Dialpad. It is
 * edited here rather than uploaded as a CSV because it changes with every joiner
 * and leaver, and a stale include-list misstates the whole report while looking
 * perfectly healthy.
 */
export function Lookups({ slug, canEdit, onNavigate }: { slug?: string; canEdit: boolean; onNavigate: (path: string) => void }) {
  const tables = useAsync(() => api.get<LookupTable[]>('/api/lookups'));

  if (tables.loading && !tables.data) return <Spinner label="Loading lists…" />;
  if (tables.error) return <Banner tone="error">{tables.error}</Banner>;

  if (!slug) {
    return (
      <>
        <div className="page-head">
          <div>
            <h1>Managed lists</h1>
            <p>Reference data the reports depend on. Editing a list changes what the next report run includes.</p>
          </div>
        </div>
        <div className="grid-cards">
          {(tables.data ?? []).map((table) => (
            <button className="card report-card" key={table.slug} onClick={() => onNavigate(`lists/${table.slug}`)}>
              <h3>{table.name}</h3>
              {table.description && <p>{table.description}</p>}
              <div className="foot">
                {table.rowCount} row{table.rowCount === 1 ? '' : 's'} · {table.columns.length} columns
              </div>
            </button>
          ))}
        </div>
      </>
    );
  }

  return <LookupGrid slug={slug} canEdit={canEdit} onBack={() => onNavigate('lists')} />;
}

function LookupGrid({ slug, canEdit, onBack }: { slug: string; canEdit: boolean; onBack: () => void }) {
  const loaded = useAsync(
    () => api.get<{ table: LookupTable; rows: Record<string, any>[] }>(`/api/lookups/${slug}/rows`),
    [slug],
  );
  const [rows, setRows] = useState<Record<string, any>[] | null>(null);
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState<{ tone: 'good' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loaded.data) setRows(loaded.data.rows.map(({ id, sort_order, ...rest }) => rest));
  }, [loaded.data]);

  if (loaded.loading && !loaded.data) return <Spinner label="Loading list…" />;
  if (loaded.error) return <Banner tone="error">{loaded.error}</Banner>;
  if (!loaded.data || !rows) return null;

  const { table } = loaded.data;

  const setCell = (index: number, column: LookupColumn, value: any) => {
    const next = [...rows];
    next[index] = { ...next[index], [column.name]: column.type === 'number' ? Number(value) : value };
    setRows(next);
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const res = await api.put<{ rowCount: number }>(`/api/lookups/${slug}/rows`, { rows });
      setMessage({ tone: 'good', text: `Saved ${res.rowCount} rows.` });
      setDirty(false);
      loaded.reload();
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  };

  const included = rows.filter((r) => r.include === true || r.include === 'true').length;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{table.name}</h1>
          {table.description && <p>{table.description}</p>}
        </div>
        <div className="page-actions">
          <button onClick={onBack}>Back to lists</button>
          {canEdit && (
            <>
              <button
                onClick={() => {
                  setRows([...rows, Object.fromEntries(table.columns.map((c) => [c.name, c.type === 'boolean' ? false : '']))]);
                  setDirty(true);
                }}
              >
                Add row
              </button>
              <button className="primary" onClick={save} disabled={busy || !dirty}>
                {busy ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
              </button>
            </>
          )}
        </div>
      </div>

      {message && <Banner tone={message.tone}>{message.text}</Banner>}
      {!canEdit && <Banner tone="info">You can view this list. Editing needs the editor role.</Banner>}

      {'include' in (rows[0] ?? {}) && (
        <Banner tone="info">
          <strong>{included}</strong> of {rows.length} people are included in reports. Anyone unticked is excluded from the
          Consultant Report entirely — that is how admin, HR and resourcers are kept out of fee-earner figures.
        </Banner>
      )}

      <div className="card">
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                {table.columns.map((c) => (
                  <th key={c.name}>
                    {c.label ?? c.name}
                    {c.required ? ' *' : ''}
                  </th>
                ))}
                {canEdit && <th />}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index}>
                  {table.columns.map((column) => (
                    <td key={column.name}>
                      {column.type === 'boolean' ? (
                        <input
                          type="checkbox"
                          disabled={!canEdit}
                          checked={row[column.name] === true || row[column.name] === 'true'}
                          onChange={(e) => setCell(index, column, e.target.checked)}
                        />
                      ) : (
                        <input
                          type={column.type === 'number' ? 'number' : 'text'}
                          disabled={!canEdit}
                          value={row[column.name] ?? ''}
                          onChange={(e) => setCell(index, column, e.target.value)}
                          style={{ minWidth: 130, border: 'none', background: 'transparent', padding: '2px 0' }}
                        />
                      )}
                    </td>
                  ))}
                  {canEdit && (
                    <td className="right">
                      <button
                        className="ghost small danger"
                        onClick={() => {
                          setRows(rows.filter((_, i) => i !== index));
                          setDirty(true);
                        }}
                        title="Remove this row"
                      >
                        Remove
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {dirty && (
          <p className="note">
            <Pill tone="warn">unsaved</Pill> Changes are not applied to reports until you save.
          </p>
        )}
      </div>
    </>
  );
}
